import { useEffect, useState } from 'react';
import { api } from './api';

// Sprecher benennen.
//
// Azure Speech trennt die Sprecher, kennt aber keine Namen. Solange im Transkript
// "Sprecher 0" steht, wird nichts nach HubSpot übertragen – ein Protokoll mit
// Platzhaltern ist am Kundenkontakt wertlos.
//
// Je Sprecher gibt es eine HubSpot-Suche (für Gesprächspartner) und die
// Möglichkeit, einen Namen frei einzutragen (für Kollegen, die nicht im CRM stehen).
export default function Speakers({ recording, onDone }) {
  const [speakers, setSpeakers] = useState([]);
  const [mapping, setMapping] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Suche je Sprecher: { [label]: { query, results, busy } }
  const [suche, setSuche] = useState({});

  useEffect(() => {
    let abgebrochen = false;
    setLoading(true);
    api
      .getSpeakers(recording.id)
      .then((d) => {
        if (abgebrochen) return;
        setSpeakers(d.speakers || []);
        setMapping(d.assigned || {});
      })
      .catch((err) => !abgebrochen && setError(err.message))
      .finally(() => !abgebrochen && setLoading(false));
    return () => {
      abgebrochen = true;
    };
  }, [recording.id]);

  // HubSpot-Suche, entprellt – sonst löst jeder Tastendruck eine Abfrage aus.
  useEffect(() => {
    const offen = Object.entries(suche).filter(
      ([, s]) => s.query && s.query.trim().length >= 2 && s.results === undefined
    );
    if (offen.length === 0) return;

    const t = setTimeout(async () => {
      for (const [label, s] of offen) {
        try {
          const results = await api.searchHubspotContacts(s.query.trim());
          setSuche((cur) => ({ ...cur, [label]: { ...cur[label], results, busy: false } }));
        } catch {
          setSuche((cur) => ({ ...cur, [label]: { ...cur[label], results: [], busy: false } }));
        }
      }
    }, 400);
    return () => clearTimeout(t);
  }, [suche]);

  const setQuery = (label, query) =>
    setSuche((cur) => ({ ...cur, [label]: { query, results: undefined, busy: query.trim().length >= 2 } }));

  const waehle = (label, kontakt) => {
    setMapping((cur) => ({ ...cur, [label]: kontakt }));
    setSuche((cur) => ({ ...cur, [label]: { query: '', results: undefined, busy: false } }));
  };

  const loesche = (label) =>
    setMapping((cur) => {
      const next = { ...cur };
      delete next[label];
      return next;
    });

  const vollstaendig =
    speakers.length > 0 && speakers.every((s) => mapping[s.label] && mapping[s.label].name);

  const speichern = async () => {
    setSaving(true);
    setError(null);
    try {
      const r = await api.saveSpeakers(recording.id, mapping);
      onDone(r);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="empty-small">Lade Sprecher …</p>;
  if (speakers.length === 0) return null;

  return (
    <div className="speakers">
      <p className="speakers-hint">
        Die Aufnahme unterscheidet {speakers.length} Sprecher, kennt aber keine Namen.
        Ordne jeden zu – danach wird das Protokoll mit den richtigen Namen neu erstellt
        und nach HubSpot übertragen.
      </p>

      {speakers.map((s) => {
        const zugeordnet = mapping[s.label];
        const st = suche[s.label] || {};
        return (
          <div className="speaker" key={s.label}>
            <div className="speaker-head">
              <span className="speaker-label">{s.label}</span>
              {zugeordnet ? (
                <span className="chip">
                  {zugeordnet.name}
                  {!zugeordnet.id && <span className="chip-tag">frei</span>}
                  <button onClick={() => loesche(s.label)} title="Zuordnung entfernen">
                    ×
                  </button>
                </span>
              ) : (
                <span className="speaker-open">nicht zugeordnet</span>
              )}
            </div>

            {s.sample && <p className="speaker-sample">„{s.sample}…"</p>}

            {!zugeordnet && (
              <>
                <div className="search-row">
                  <input
                    value={st.query || ''}
                    onChange={(e) => setQuery(s.label, e.target.value)}
                    placeholder="In HubSpot suchen – oder Namen eintippen und Enter"
                    onKeyDown={(e) => {
                      // Enter ohne Treffer: Name frei übernehmen. Nicht jeder steht
                      // im CRM – eigene Kollegen etwa fast nie.
                      if (e.key === 'Enter' && (st.query || '').trim()) {
                        e.preventDefault();
                        waehle(s.label, { name: st.query.trim(), id: null, email: null });
                      }
                    }}
                  />
                  {st.busy && <span className="search-status">Suche …</span>}
                </div>

                {st.results && st.results.length > 0 && (
                  <div className="results">
                    {st.results.map((c) => (
                      <div className="result" key={c.id}>
                        <div>
                          <div>{c.name}</div>
                          <div className="result-sub">
                            {[c.email, c.company].filter(Boolean).join(' · ')}
                          </div>
                        </div>
                        <button className="btn ghost small" onClick={() => waehle(s.label, c)}>
                          Zuordnen
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {st.results && st.results.length === 0 && (st.query || '').trim().length >= 2 && (
                  <div className="no-results">
                    <span>Kein HubSpot-Kontakt gefunden.</span>
                    <button
                      className="btn small"
                      onClick={() => waehle(s.label, { name: st.query.trim(), id: null, email: null })}
                    >
                      „{st.query.trim()}" als Namen übernehmen
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}

      {error && <p className="error">{error}</p>}

      <div className="pane-actions">
        <button className="btn primary" onClick={speichern} disabled={saving || !vollstaendig}>
          {saving
            ? 'Speichere und übertrage …'
            : vollstaendig
              ? 'Zuordnen und nach HubSpot übertragen'
              : 'Erst alle Sprecher zuordnen'}
        </button>
      </div>
    </div>
  );
}
